import { AlertCircle, Bell, CheckCircle2, Info } from 'lucide-react';
import type { Notification } from '../types';

interface NotificationListProps {
  notifications: Notification[];
}

function statusBadgeClass(status: string): string {
  if (status === 'sent') {
    return 'bg-emerald-100 text-emerald-800';
  }
  if (status === 'failed') {
    return 'bg-red-100 text-red-800';
  }
  if (status === 'queued') {
    return 'bg-amber-100 text-amber-800';
  }
  return 'bg-gray-100 text-gray-800';
}

function statusIcon(status: string) {
  if (status === 'sent') {
    return <CheckCircle2 className="w-5 h-5 text-emerald-600" />;
  }
  if (status === 'failed') {
    return <AlertCircle className="w-5 h-5 text-red-600" />;
  }
  if (status === 'queued') {
    return <Info className="w-5 h-5 text-amber-600" />;
  }
  return <Bell className="w-5 h-5 text-gray-600" />;
}

export function NotificationList({ notifications }: NotificationListProps) {
  if (notifications.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          <Bell className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No notifications</h3>
        <p className="text-gray-500">You&apos;re all caught up!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className="relative p-4 rounded-lg border border-gray-200 bg-white"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg flex-shrink-0 bg-gray-50">
                {statusIcon(notification.status)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900">Alert Notification</h3>
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full ${statusBadgeClass(notification.status)}`}
                      >
                        {notification.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-1">
                      {notification.message || 'No message body'}
                    </p>
                    {notification.errorMessage && (
                      <p className="text-sm text-red-600 mt-1">
                        Error: {notification.errorMessage}
                      </p>
                    )}
                  </div>
                </div>

                <p className="text-xs text-gray-400 mt-2">
                  Created {new Date(notification.createdAt).toLocaleString()}
                  {notification.sentAt
                    ? ` • Sent ${new Date(notification.sentAt).toLocaleString()}`
                    : ''}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
